import { BarChart3, X } from "lucide-react";
import { NeuigkeitUmfrage } from "./useAdminNeuigkeiten";

/** Zwei bis sechs - unter zwei ist es keine Frage, ueber sechs liest sie niemand zu Ende. */
export const MIN_UMFRAGE_OPTIONEN = 2;
export const MAX_UMFRAGE_OPTIONEN = 6;

export const leereUmfrage = (): NeuigkeitUmfrage => ({ frage: "", optionen: ["", ""], mehrfach: false, betriebeZeigen: false });

const Schalter = ({ an, label, erklaerung, onToggle }: { an: boolean; label: string; erklaerung: string; onToggle: () => void }) => (
  <div className="flex flex-col gap-1">
    <button type="button" aria-pressed={an} onClick={onToggle} className="flex items-center gap-2.5 text-left text-[13px] text-gray-700">
      <span
        aria-hidden="true"
        className={`flex h-5 w-9 flex-none items-center rounded-full p-0.5 transition-colors ${an ? "bg-primary" : "bg-gray-300"}`}
      >
        <span className={`block h-4 w-4 rounded-full bg-white transition-all ${an ? "ml-3.5" : "ml-0"}`} />
      </span>
      {label}
    </button>
    <p className="m-0 pl-[46px] text-[11px] leading-relaxed text-gray-400">{erklaerung}</p>
  </div>
);

/**
 * Die Umfrage in der Schreibmaske (KAN-302).
 *
 * Sie steht zwischen den Videos und den Knoepfen, als gestrichelter Block - wie die
 * Platzhalter daneben, damit sichtbar bleibt, dass hier etwas angelegt wird und nicht
 * schon etwas steht.
 *
 * Die beiden Schalter gelten je Umfrage, nicht fuer alle: Eine Vertriebsfrage will die
 * Betriebe nennen, eine Zufriedenheitsfrage darf es nicht - sonst antwortet niemand
 * ehrlich.
 */
export const UmfrageFelder = ({
  umfrage,
  onChange,
  onEntfernen,
}: {
  umfrage: NeuigkeitUmfrage;
  onChange: (umfrage: NeuigkeitUmfrage) => void;
  onEntfernen: () => void;
}) => {
  const optionSetzen = (index: number, label: string): void =>
    onChange({ ...umfrage, optionen: umfrage.optionen.map((vorher, nummer) => (nummer === index ? label : vorher)) });

  const voll = umfrage.optionen.length >= MAX_UMFRAGE_OPTIONEN;

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/70 p-3.5">
      <div className="flex items-center justify-between gap-2.5">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neuigkeit">
          <BarChart3 size={14} strokeWidth={2} aria-hidden="true" />
          Umfrage
        </span>
        <button type="button" onClick={onEntfernen} className="text-xs text-gray-500 underline underline-offset-2 hover:text-gray-700">
          Umfrage entfernen
        </button>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-gray-600">Frage</span>
        <input
          type="text"
          value={umfrage.frage}
          onChange={event => onChange({ ...umfrage, frage: event.target.value })}
          placeholder="Woran sollen wir als Nächstes arbeiten?"
          maxLength={200}
          className="min-h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-[13px] text-gray-900 placeholder:text-gray-400"
        />
      </label>

      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-gray-600">Antwortmöglichkeiten</span>
        {umfrage.optionen.map((option, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="w-4 flex-none text-right text-xs text-gray-400">{index + 1}</span>
            <input
              type="text"
              value={option}
              onChange={event => optionSetzen(index, event.target.value)}
              aria-label={`Antwort ${index + 1}`}
              maxLength={120}
              className="min-h-9 w-full min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 text-[13px] text-gray-900 placeholder:text-gray-400"
            />
            <button
              type="button"
              aria-label={`Antwort ${index + 1} entfernen`}
              disabled={umfrage.optionen.length <= MIN_UMFRAGE_OPTIONEN}
              onClick={() => onChange({ ...umfrage, optionen: umfrage.optionen.filter((_, nummer) => nummer !== index) })}
              className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 enabled:hover:border-red-200 enabled:hover:bg-red-50 enabled:hover:text-red-700 disabled:opacity-40"
            >
              <X size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={voll}
          onClick={() => onChange({ ...umfrage, optionen: [...umfrage.optionen, ""] })}
          className="min-h-8 self-start rounded-lg border border-dashed border-gray-300 bg-white px-2.5 text-xs text-gray-700 disabled:text-gray-400"
        >
          + Antwort hinzufügen
        </button>
        <span className="text-[11px] text-gray-400">
          {voll ? "Mehr als sechs liest niemand zu Ende." : `${umfrage.optionen.length} von höchstens ${MAX_UMFRAGE_OPTIONEN} · mindestens ${MIN_UMFRAGE_OPTIONEN}`}
        </span>
      </div>

      <div className="flex flex-col gap-2.5 border-t border-gray-200 pt-3">
        <Schalter
          an={umfrage.mehrfach}
          label="Mehrere Antworten erlauben"
          erklaerung={
            umfrage.mehrfach
              ? "Der Kunde kann mehrere Antworten ankreuzen. Die Anteile ergeben dann zusammen mehr als 100 Prozent."
              : "Der Kunde wählt genau eine Antwort."
          }
          onToggle={() => onChange({ ...umfrage, mehrfach: !umfrage.mehrfach })}
        />
        <Schalter
          an={umfrage.betriebeZeigen}
          label="Betriebe in der Auswertung zeigen"
          erklaerung={
            umfrage.betriebeZeigen
              ? 'Unter der Frage steht „Deine Antwort geht an das kandidat.io-Team“. Für Fragen, bei denen du nachfassen willst.'
              : 'Unter der Frage steht „Deine Antwort wird anonym ausgewertet“. Für Fragen, bei denen Ehrlichkeit zählt.'
          }
          onToggle={() => onChange({ ...umfrage, betriebeZeigen: !umfrage.betriebeZeigen })}
        />
      </div>
    </div>
  );
};

export default UmfrageFelder;
